import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import InternationalSEO from '../../components/seo/InternationalSEO';

export default function Pricing() {
  const [selectedRoute, setSelectedRoute] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bookingData, setBookingData] = useState({
    departureDate: '',
    returnDate: '',
    passengers: 1,
    travelType: 'round-trip',
    airline: '',
    paymentMethod: '',
    customerName: '',
    customerEmail: '',
  });

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleRouteSelection = (route: any) => {
    setSelectedRoute(route);
    // Scroll to booking section when route is selected
    setTimeout(() => {
      const bookingSection = document.getElementById('booking-section');
      if (bookingSection) {
        bookingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleStripePayment = async () => {
    // ---------------------------------------------------------------
    // Basic validation
    // ---------------------------------------------------------------
    if (
      !selectedRoute ||
      !bookingData.departureDate ||
      !bookingData.airline ||
      !bookingData.customerName ||
      !bookingData.customerEmail
    ) {
      alert(
        'Please fill in all required booking details including your name and email'
      );
      return;
    }

    // ---------------------------------------------------------------
    // Date validation
    // ---------------------------------------------------------------
    const today = new Date();
    const departureDate = new Date(bookingData.departureDate);
    if (departureDate < today) {
      alert('Please select a departure date that is today or in the future');
      return;
    }

    if (
      bookingData.travelType === 'round-trip' &&
      bookingData.returnDate
    ) {
      const returnDate = new Date(bookingData.returnDate);
      if (returnDate <= departureDate) {
        alert('Return date must be after departure date');
        return;
      }
    }

    setIsProcessing(true);

    try {
      const selectedAirline = airlines.find(
        (a) => a.code === bookingData.airline
      );
      const selectedClass =
        pricingData
          .find((p) =>
            p.routes.some((r) => r.route === selectedRoute.route)
          )
          ?.class?.trim() || 'Economy Class';

      console.log('Initiating payment for:', {
        route: selectedRoute.route,
        totalPrice: selectedRoute.totalPrice * bookingData.passengers,
        passengers: bookingData.passengers,
        customerEmail: bookingData.customerEmail,
        customerName: bookingData.customerName
      });

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-stripe-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            route: selectedRoute.route,
            totalPrice: selectedRoute.totalPrice,
            passengers: bookingData.passengers,
            departureDate: bookingData.departureDate,
            returnDate: bookingData.returnDate,
            travelType: bookingData.travelType,
            airline: selectedAirline?.name || bookingData.airline,
            flightClass: selectedClass,
            customerEmail: bookingData.customerEmail,
            customerName: bookingData.customerName,
          }),
        }
      );

      console.log('Payment API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Payment API error:', errorText);
        throw new Error('Payment setup failed');
      }

      const data = await response.json();
      console.log('Payment response data:', data);

      if (data.success && data.url) {
        console.log('Redirecting to Stripe checkout:', data.url);
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error('Payment setup failed:', data);
        alert(data.error || 'Payment setup failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment setup failed. Please check your information and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBookingSubmit = () => {
    if (
      !selectedRoute ||
      !bookingData.departureDate ||
      !bookingData.airline ||
      !bookingData.paymentMethod
    ) {
      alert('Please fill in all required booking details');
      return;
    }

    alert(
      `Booking initiated for ${selectedRoute.route} on ${bookingData.departureDate} with ${
        airlines.find((a) => a.code === bookingData.airline)?.name
      } using ${
        paymentMethods.find((p) => p.id === bookingData.paymentMethod)?.name
      }`
    );
  };

  // -------------------------------------------------------------------------
  // Static data
  // -------------------------------------------------------------------------
  const airlines = [
    // Major International Airlines
    { code: 'AA', name: 'American Airlines', logo: 'ri-plane-fill' },
    { code: 'DL', name: 'Delta Airlines', logo: 'ri-plane-fill' },
    { code: 'UA', name: 'United Airlines', logo: 'ri-plane-fill' },
    { code: 'BA', name: 'British Airways', logo: 'ri-plane-fill' },
    { code: 'LH', name: 'Lufthansa', logo: 'ri-plane-fill' },
    { code: 'AF', name: 'Air France', logo: 'ri-plane-fill' },
    { code: 'EK', name: 'Emirates', logo: 'ri-plane-fill' },
    { code: 'SQ', name: 'Singapore Airlines', logo: 'ri-plane-fill' },

    // European Airlines
    { code: 'KL', name: 'KLM Royal Dutch Airlines', logo: 'ri-plane-fill' },
    { code: 'LX', name: 'Swiss International Air Lines', logo: 'ri-plane-fill' },
    { code: 'OS', name: 'Austrian Airlines', logo: 'ri-plane-fill' },
    { code: 'SN', name: 'Brussels Airlines', logo: 'ri-plane-fill' },
    { code: 'SK', name: 'Scandinavian Airlines', logo: 'ri-plane-fill' },
    { code: 'AY', name: 'Finnair', logo: 'ri-plane-fill' },
    { code: 'IB', name: 'Iberia', logo: 'ri-plane-fill' },
    { code: 'TP', name: 'TAP Air Portugal', logo: 'ri-plane-fill' },
    { code: 'AZ', name: 'Alitalia', logo: 'ri-plane-fill' },
    { code: 'FR', name: 'Ryanair', logo: 'ri-plane-fill' },
    { code: 'U2', name: 'easyJet', logo: 'ri-plane-fill' },
    { code: 'VY', name: 'Vueling Airlines', logo: 'ri-plane-fill' },
    { code: 'W6', name: 'Wizz Air', logo: 'ri-plane-fill' },
    { code: 'EI', name: 'Aer Lingus', logo: 'ri-plane-fill' },

    // Asian Airlines
    { code: 'CX', name: 'Cathay Pacific', logo: 'ri-plane-fill' },
    { code: 'JL', name: 'Japan Airlines', logo: 'ri-plane-fill' },
    { code: 'NH', name: 'All Nippon Airways', logo: 'ri-plane-fill' },
    { code: 'KE', name: 'Korean Air', logo: 'ri-plane-fill' },
    { code: 'OZ', name: 'Asiana Airlines', logo: 'ri-plane-fill' },
    { code: 'CA', name: 'Air China', logo: 'ri-plane-fill' },
    { code: 'MU', name: 'China Eastern Airlines', logo: 'ri-plane-fill' },
    { code: 'CZ', name: 'China Southern Airlines', logo: 'ri-plane-fill' },
    { code: 'TG', name: 'Thai Airways', logo: 'ri-plane-fill' },
    { code: 'MH', name: 'Malaysia Airlines', logo: 'ri-plane-fill' },
    { code: 'GA', name: 'Garuda Indonesia', logo: 'ri-plane-fill' },
    { code: 'PR', name: 'Philippine Airlines', logo: 'ri-plane-fill' },
    { code: 'VN', name: 'Vietnam Airlines', logo: 'ri-plane-fill' },
    { code: 'AI', name: 'Air India', logo: 'ri-plane-fill' },
    { code: '6E', name: 'IndiGo', logo: 'ri-plane-fill' },
    { code: 'SG', name: 'SpiceJet', logo: 'ri-plane-fill' },

    // Middle Eastern Airlines
    { code: 'QR', name: 'Qatar Airways', logo: 'ri-plane-fill' },
    { code: 'EY', name: 'Etihad Airways', logo: 'ri-plane-fill' },
    { code: 'MS', name: 'EgyptAir', logo: 'ri-plane-fill' },
    { code: 'RJ', name: 'Royal Jordanian', logo: 'ri-plane-fill' },
    { code: 'GF', name: 'Gulf Air', logo: 'ri-plane-fill' },
    { code: 'WY', name: 'Oman Air', logo: 'ri-plane-fill' },
    { code: 'SV', name: 'Saudi Arabian Airlines', logo: 'ri-plane-fill' },
    { code: 'TK', name: 'Turkish Airlines', logo: 'ri-plane-fill' },

    // African Airlines
    { code: 'ET', name: 'Ethiopian Airlines', logo: 'ri-plane-fill' },
    { code: 'SA', name: 'South African Airways', logo: 'ri-plane-fill' },
    { code: 'KQ', name: 'Kenya Airways', logo: 'ri-plane-fill' },
    { code: 'AT', name: 'Royal Air Maroc', logo: 'ri-plane-fill' },
    { code: 'UU', name: 'Air Austral', logo: 'ri-plane-fill' },
    { code: 'W3', name: 'Arik Air', logo: 'ri-plane-fill' },
    { code: 'N0', name: 'Air Peace', logo: 'ri-plane-fill' },
    { code: 'DN', name: 'Dana Air', logo: 'ri-plane-fill' },
    { code: 'AXK', name: "Air Côte d'Ivoire", logo: 'ri-plane-fill' },

    // North American Airlines
    { code: 'AC', name: 'Air Canada', logo: 'ri-plane-fill' },
    { code: 'WS', name: 'WestJet', logo: 'ri-plane-fill' },
    { code: 'AS', name: 'Alaska Airlines', logo: 'ri-plane-fill' },
    { code: 'B6', name: 'JetBlue Airways', logo: 'ri-plane-fill' },
    { code: 'WN', name: 'Southwest Airlines', logo: 'ri-plane-fill' },
    { code: 'F9', name: 'Frontier Airlines', logo: 'ri-plane-fill' },
    { code: 'NK', name: 'Spirit Airlines', logo: 'ri-plane-fill' },
    { code: 'G4', name: 'Allegiant Air', logo: 'ri-plane-fill' },
    { code: 'AM', name: 'Aeroméxico', logo: 'ri-plane-fill' },
    { code: 'VB', name: 'VivaAerobus', logo: 'ri-plane-fill' },
    { code: 'Y4', name: 'Volaris', logo: 'ri-plane-fill' },

    // South American Airlines
    { code: 'LA', name: 'LATAM Airlines', logo: 'ri-plane-fill' },
    { code: 'G3', name: 'Gol Linhas Aéreas', logo: 'ri-plane-fill' },
    { code: 'AD', name: 'Azul Brazilian Airlines', logo: 'ri-plane-fill' },
    { code: 'AR', name: 'Aerolíneas Argentinas', logo: 'ri-plane-fill' },
    { code: 'AV', name: 'Avianca', logo: 'ri-plane-fill' },
    { code: 'CM', name: 'Copa Airlines', logo: 'ri-plane-fill' },
    { code: 'LP', name: 'LAN Peru', logo: 'ri-plane-fill' },
    { code: 'H2', name: 'Sky Airline', logo: 'ri-plane-fill' },

    // Oceania Airlines
    { code: 'QF', name: 'Qantas', logo: 'ri-plane-fill' },
    { code: 'VA', name: 'Virgin Australia', logo: 'ri-plane-fill' },
    { code: 'JQ', name: 'Jetstar Airways', logo: 'ri-plane-fill' },
    { code: 'NZ', name: 'Air New Zealand', logo: 'ri-plane-fill' },
    { code: 'FJ', name: 'Fiji Airways', logo: 'ri-plane-fill' },

    // Low-Cost Carriers
    { code: 'PC', name: 'Pegasus Airlines', logo: 'ri-plane-fill' },
    { code: 'XQ', name: 'SunExpress', logo: 'ri-plane-fill' },
    { code: 'HV', name: 'Transavia', logo: 'ri-plane-fill' },
    { code: 'EW', name: 'Eurowings', logo: 'ri-plane-fill' },
    { code: 'LS', name: 'Jet2.com', logo: 'ri-plane-fill' },
    { code: 'MT', name: 'Thomas Cook Airlines', logo: 'ri-plane-fill' },
    { code: 'BY', name: 'TUI Airways', logo: 'ri-plane-fill' },

    // Cargo Airlines
    { code: 'FX', name: 'FedEx Express (Cargo)', logo: 'ri-truck-line' },
    { code: 'UPS', name: 'UPS Airlines (Cargo)', logo: 'ri-truck-line' }
  ];

  const paymentMethods = [
    { id: 'visa', name: 'Visa Credit Card', icon: 'ri-bank-card-line' },
    { id: 'mastercard', name: 'Mastercard Credit Card', icon: 'ri-bank-card-line' },
    { id: 'amex', name: 'American Express', icon: 'ri-bank-card-line' },
    { id: 'discover', name: 'Discover Card', icon: 'ri-bank-card-line' },
    { id: 'debit', name: 'Debit Card', icon: 'ri-bank-card-2-line' },
    { id: 'bank', name: 'Bank Transfer', icon: 'ri-bank-line' },
  ];

  const pricingData = [
    {
      class: 'First Class',
      icon: 'ri-vip-crown-line',
      description: 'Ultimate luxury and comfort for your journey',
      routes: [
        {
          route: 'New York → London',
          basePrice: 4890,
          totalPrice: 4939,
          duration: '7h 15m',
          layover: 'Direct Flight',
          itinerary: [
            { time: '10:30 PM', location: 'JFK - New York', action: 'Departure' },
            { time: '9:45 AM+1', location: 'LHR - London', action: 'Arrival' },
          ],
        },
        // ... other first‑class routes omitted for brevity ...
      ],
      features: [
        'Private suites with lie-flat beds',
        'Gourmet dining experience',
        'Premium lounge access',
        'Dedicated flight attendant',
        'Priority boarding and check-in',
        'Luxury amenity kits',
      ],
    },
    {
      class: 'Business Class',
      icon: 'ri-briefcase-line',
      description: 'Perfect balance of comfort and productivity',
      routes: [
        {
          route: 'New York → London',
          basePrice: 2890,
          totalPrice: 2919,
          duration: '7h 15m',
          layover: 'Direct Flight',
          itinerary: [
            { time: '8:30 PM', location: 'JFK - New York', action: 'Departure' },
            { time: '7:45 AM+1', location: 'LHR - London', action: 'Arrival' },
          ],
        },
        // ... other business‑class routes omitted for brevity ...
      ],
      features: [
        'Lie-flat seats',
        'Premium meal service',
        'Business lounge access',
        'Extra baggage allowance',
        'Priority services',
        'In-flight entertainment',
      ],
    },
    {
      class: 'Economy Class',
      icon: 'ri-plane-line',
      description: 'Comfortable and affordable travel worldwide',
      routes: [
        {
          route: 'New York → London',
          basePrice: 590,
          totalPrice: 596,
          duration: '7h 45m',
          layover: '1h 45m in Boston',
          itinerary: [
            { time: '6:30 PM', location: 'JFK - New York', action: 'Departure' },
            { time: '8:15 PM', location: 'BOS - Boston', action: 'Layover (1h 45m)' },
            { time: '10:00 PM', location: 'BOS - Boston', action: 'Departure' },
            { time: '8:15 AM+1', location: 'LHR - London', action: 'Arrival' },
          ],
        },
        // ... other economy‑class routes omitted for brevity ...
      ],
      features: [
        'Comfortable seating',
        'Complimentary meals',
        'Personal entertainment',
        'Standard baggage',
        'Quality service',
        'Global destinations',
      ],
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-900">
      <InternationalSEO 
        page="pricing"
        title="Flight Booking Prices & Airline Tickets | MAVESKY TRAVELS Transparent Pricing"
        description="Compare flight prices and book airline tickets with MAVESKY TRAVELS. Transparent 1% agency fee on all flights. First Class, Business Class, and Economy Class options available. Book cheap flights to worldwide destinations with competitive pricing and professional service."
        keywords="flight prices, airline tickets, flight booking prices, cheap flights, flight deals, transparent pricing, first class flights, business class flights, economy flights, international flight prices, domestic flight costs, MAVESKY TRAVELS pricing"
        canonicalUrl="https://maveskytravels.com/pricing"
      />
      
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Book Your <strong>Flight</strong>
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Choose from <strong>60+ airlines</strong> worldwide. <strong>Transparent pricing</strong> with just a 
              <strong>1% agency fee</strong>. <strong>Professional service</strong> for all your <strong>travel needs</strong>.
            </p>
          </div>
        </section>

        {/* --------------------------------------------------- Booking Form --------------------------------------------------- */}
        <section className="py-10 bg-gray-800/30">
          <div className="container mx-auto px-6">
            <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
              <h2 className="text-2xl font-bold text-white mb-6">
                Book Your Flight
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={bookingData.customerName}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        customerName: e.target.value,
                      })
                    }
                    placeholder="Enter your full name"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={bookingData.customerEmail}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        customerEmail: e.target.value,
                      })
                    }
                    placeholder="Enter your email"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Travel Type
                  </label>
                  <select
                    value={bookingData.travelType}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        travelType: e.target.value,
                      })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 pr-8"
                  >
                    <option value="round-trip">Round Trip</option>
                    <option value="one-way">One Way</option>
                    <option value="multi-city">Multi City</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Departure Date *
                  </label>
                  <input
                    type="date"
                    value={bookingData.departureDate}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        departureDate: e.target.value,
                      })
                    }
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                {bookingData.travelType === 'round-trip' && (
                  <div>
                    <label className="block text-white font-medium mb-2">
                      Return Date
                    </label>
                    <input
                      type="date"
                      value={bookingData.returnDate}
                      onChange={(e) =>
                        setBookingData({
                          ...bookingData,
                          returnDate: e.target.value,
                        })
                      }
                      min={
                        bookingData.departureDate ||
                        new Date().toISOString().split('T')[0]
                      }
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-white font-medium mb-2">
                    Passengers
                  </label>
                  <select
                    value={bookingData.passengers}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        passengers: parseInt(e.target.value, 10),
                      })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 pr-8"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'Passenger' : 'Passengers'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Preferred Airline *
                  </label>
                  <select
                    value={bookingData.airline}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        airline: e.target.value,
                      })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Airline</option>
                    {airlines.map((airline) => (
                      <option key={airline.code} value={airline.code}>
                        {airline.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Secure Payment Badge */}
              <div className="flex items-center justify-center bg-green-900/30 border border-green-800 rounded-lg p-4 mb-6">
                <div className="w-6 h-6 flex items-center justify-center mr-3">
                  <i className="ri-shield-check-line text-green-400 text-xl"></i>
                </div>
                <span className="text-green-300 font-medium">
                  Secure Payment Processing by Stripe
                </span>
                <div className="ml-4 flex items-center space-x-2">
                  <i className="ri-bank-card-line text-green-400"></i>
                  <i className="ri-lock-line text-green-400"></i>
                  <i className="ri-verified-badge-line text-green-400"></i>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------- Pricing Cards ---------------------------------------------------- */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="space-y-16">
              {/* ---- Class cards ---- */}
              {pricingData.map((classData, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700"
                >
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-xl mr-4">
                      <i className={`${classData.icon} text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">
                        {classData.class}
                      </h2>
                      <p className="text-gray-400">{classData.description}</p>
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* ---- Routes ---- */}
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-6">
                        Popular Routes
                      </h3>
                      <div className="space-y-4">
                        {classData.routes.map((route, routeIndex) => (
                          <div
                            key={routeIndex}
                            className="bg-gray-700/50 rounded-lg p-4"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <div className="text-white font-medium text-lg">
                                  {route.route}
                                </div>
                                <div className="text-sm text-gray-400">
                                  Duration: {route.duration} | {route.layover}
                                </div>
                                <div className="text-sm text-gray-400">
                                  Airline Price: ${route.basePrice.toLocaleString()} + 1%
                                  Fee
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-blue-400">
                                  ${route.totalPrice.toLocaleString()}
                                </div>
                                <button
                                  onClick={() => handleRouteSelection(route)}
                                  className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded text-sm transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  Select Route
                                </button>
                              </div>
                            </div>

                            {/* Itinerary */}
                            <div className="mt-4 pt-4 border-t border-gray-600">
                              <h4 className="text-white font-medium mb-2">
                                Itinerary
                              </h4>
                              <div className="space-y-2">
                                {route.itinerary.map((item, itemIndex) => (
                                  <div
                                    key={itemIndex}
                                    className="flex items-center text-sm"
                                  >
                                    <div className="w-16 text-gray-300 font-mono">
                                      {item.time}
                                    </div>
                                    <div className="w-4 h-4 flex items-center justify-center mx-3">
                                      <i
                                        className={`ri-${
                                          item.action.includes('Departure')
                                            ? 'takeoff'
                                            : item.action.includes('Arrival')
                                            ? 'flight-land'
                                            : 'time'
                                        }-line text-blue-400`}
                                      ></i>
                                    </div>
                                    <div className="text-gray-300">
                                      {item.location}
                                    </div>
                                    <div className="ml-auto text-gray-400">
                                      {item.action}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ---- Features ---- */}
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-6">
                        What's Included
                      </h3>
                      <div className="grid grid-cols-1 gap-3 mb-8">
                        {classData.features.map((feature, featureIndex) => (
                          <div
                            key={featureIndex}
                            className="flex items-center text-gray-300"
                          >
                            <div className="w-5 h-5 flex items-center justify-center mr-3">
                              <i className="ri-check-line text-green-400"></i>
                            </div>
                            {feature}
                          </div>
                        ))}
                      </div>

                      {/* Payment methods list */}
                      <div className="bg-gray-700/30 rounded-lg p-4">
                        <h4 className="text-white font-medium mb-3">
                          Accepted Payment Methods
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {paymentMethods.map((method) => (
                            <div
                              key={method.id}
                              className="flex items-center text-sm text-gray-300"
                            >
                              <div className="w-4 h-4 flex items-center justify-center mr-2">
                                <i
                                  className={`${method.icon} text-blue-400`}
                                ></i>
                              </div>
                              {method.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* ---- Selected Route Booking ---- */}
              {selectedRoute && (
                <div
                  id="booking-section"
                  className="container mx-auto px-6 mt-16"
                >
                  <div className="bg-blue-900/30 border border-blue-800 rounded-xl p-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-white">
                        Complete Your Booking
                      </h3>
                      <button
                        onClick={() => setSelectedRoute(null)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>

                    {/* Selected Route Display */}
                    <div className="bg-green-900/30 border border-green-800 rounded-lg p-4 mb-6">
                      <div className="flex items-center">
                        <div className="w-6 h-6 flex items-center justify-center mr-3">
                          <i className="ri-check-line text-green-400 text-xl"></i>
                        </div>
                        <div>
                          <span className="text-green-300 font-medium">
                            Selected Route:{' '}
                          </span>
                          <span className="text-white font-semibold">
                            {selectedRoute.route}
                          </span>
                          <span className="text-green-300 ml-4">
                            ${selectedRoute.totalPrice.toLocaleString()} per
                            person
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Booking Summary */}
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                      <h4 className="text-white font-medium mb-4">
                        Booking Summary
                      </h4>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-gray-300">
                              <span>Route:</span>
                              <span className="text-white font-medium">
                                {selectedRoute.route}
                              </span>
                            </div>
                            <div className="flex justify-between text-gray-300">
                              <span>Duration:</span>
                              <span className="text-white">
                                {selectedRoute.duration}
                              </span>
                            </div>
                            <div className="flex justify-between text-gray-300">
                              <span>Flight Type:</span>
                              <span className="text-white">
                                {selectedRoute.layover}
                              </span>
                            </div>
                            <div className="flex justify-between text-gray-300">
                              <span>Passengers:</span>
                              <span className="text-white">
                                {bookingData.passengers}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-gray-300">
                              <span>Travel Type:</span>
                              <span className="text-white capitalize">
                                {bookingData.travelType.replace('-', ' ')}
                              </span>
                            </div>
                            <div className="flex justify-between text-gray-300">
                              <span>Departure Date:</span>
                              <span className="text-white">
                                {bookingData.departureDate || 'Not selected'}
                              </span>
                            </div>
                            {bookingData.travelType === 'round-trip' && (
                              <div className="flex justify-between text-gray-300">
                                <span>Return Date:</span>
                                <span className="text-white">
                                  {bookingData.returnDate || 'Not selected'}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between text-gray-300">
                              <span>Airline:</span>
                              <span className="text-white">
                                {airlines.find(
                                  (a) => a.code === bookingData.airline
                                )?.name || 'Not selected'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-gray-600 mt-4 pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-xl font-semibold text-white">
                            Total Amount:
                          </span>
                          <span className="text-3xl font-bold text-blue-400">
                            $
                            {(
                              selectedRoute.totalPrice *
                              bookingData.passengers
                            ).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-1">
                          Includes airline price + 1% agency fee
                        </p>
                      </div>
                    </div>

                    {/* Payment Button */}
                    <div className="text-center">
                      <button
                        onClick={handleStripePayment}
                        disabled={
                          isProcessing ||
                          !bookingData.departureDate ||
                          !bookingData.airline ||
                          !bookingData.customerName ||
                          !bookingData.customerEmail
                        }
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white px-12 py-4 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap flex items-center justify-center mx-auto text-lg shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                      >
                        {isProcessing ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-3 text-xl"></i>
                            Processing Payment...
                          </>
                        ) : (
                          <>
                            <i className="ri-secure-payment-line mr-3 text-xl"></i>
                            Pay ${(selectedRoute.totalPrice * bookingData.passengers).toLocaleString()} with Stripe
                          </>
                        )}
                      </button>

                      <div className="flex items-center justify-center mt-4 space-x-4">
                        <div className="flex items-center text-green-400">
                          <i className="ri-shield-check-line mr-1"></i>
                          <span className="text-sm">256-bit SSL</span>
                        </div>
                        <div className="flex items-center text-green-400">
                          <i className="ri-lock-line mr-1"></i>
                          <span className="text-sm">PCI Compliant</span>
                        </div>
                        <div className="flex items-center text-green-400">
                          <i className="ri-verified-badge-line mr-1"></i>
                          <span className="text-sm">Secure</span>
                        </div>
                      </div>

                      {(!bookingData.departureDate ||
                        !bookingData.airline ||
                        !bookingData.customerName ||
                        !bookingData.customerEmail) && (
                        <div className="mt-4 p-4 bg-red-900/30 border border-red-800 rounded-lg">
                          <div className="flex items-center justify-center text-red-400 mb-2">
                            <i className="ri-error-warning-line mr-2"></i>
                            <span className="font-medium">Required Information Missing</span>
                          </div>
                          <div className="text-sm text-red-300 space-y-1">
                            {!bookingData.customerName && <div>• Please enter your full name</div>}
                            {!bookingData.customerEmail && <div>• Please enter your email address</div>}
                            {!bookingData.departureDate && <div>• Please select a departure date</div>}
                            {!bookingData.airline && <div>• Please select your preferred airline</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ---- Pricing Note ---- */}
              <div className="mt-16 bg-blue-900/30 border border-blue-800 rounded-xl p-8 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <i className="ri-information-line text-xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Our Booking Process
                </h3>
                <p className="text-gray-300 max-w-2xl mx-auto mb-4">
                  We add a transparent 1% agency fee to current airline
                  prices. Prices are updated daily to reflect the latest
                  airline rates. No hidden fees, no surprises.
                </p>
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-2">
                      <i className="ri-calendar-line text-white"></i>
                    </div>
                    <div className="text-white font-medium">Select Dates</div>
                    <div className="text-gray-400 text-sm">
                      Choose your travel dates
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-2">
                      <i className="ri-plane-line text-white"></i>
                    </div>
                    <div className="text-white font-medium">Pick Airline</div>
                    <div className="text-gray-400 text-sm">
                      Select preferred airline
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-2">
                      <i className="ri-bank-card-line text-white"></i>
                    </div>
                    <div className="text-white font-medium">Secure Payment</div>
                    <div className="text-gray-400 text-sm">
                      Complete with payment method
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
